
// utils/api/index.ts
import { ApiResponse, Method, RequestConfig } from "./ApiProps";
import "react-toastify/dist/ReactToastify.css";

// Client-side token helper for Vite
function getClientAuthToken(): string | null {
  try {
    // Check cookies for token
    const cookies = document.cookie.split(';');
    const tokenCookie = cookies.find(cookie => cookie.trim().startsWith('crm_auth_token='));
    
    if (tokenCookie) {
      return tokenCookie.split('=')[1];
    }

    // Fallback to Vite environment variable
    if (import.meta.env.VITE_BEARER_TOKEN) {
      return import.meta.env.VITE_BEARER_TOKEN;
    }
    
    return null;
  } catch (error) {
    console.error("Error getting client auth token:", error);
    // Try fallback even on error
    return import.meta.env.VITE_BEARER_TOKEN || null;
  }
}

// Define the HTTP methods as a constant
export const HTTP_METHODS = {
  GET: "GET" as Method,
  POST: "POST" as Method,
  PUT: "PUT" as Method,
  DELETE: "DELETE" as Method,
  PATCH: "PATCH" as Method,
} as const;

class Api {
  private baseUrl: string;
  public Method: typeof HTTP_METHODS;

  constructor() {
    this.baseUrl =
      import.meta.env.VITE_API_URL || "https://api.growtheye.com/api";

    // Add validation to ensure baseUrl is not empty
    if (!this.baseUrl || this.baseUrl === "") {
      console.error("❌ API_URL is not configured!");
      throw new Error("API base URL is not configured. Please set VITE_API_URL environment variable.");
    }

    this.Method = HTTP_METHODS;
  }

  private async getHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    try {
      // Get token from cookies or environment
      const token = getClientAuthToken();

      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    } catch (error) {
      // Try fallback token even if there's an error
      if (import.meta.env.VITE_BEARER_TOKEN) {
        headers["Authorization"] = `Bearer ${import.meta.env.VITE_BEARER_TOKEN}`;
      }
    }

    return headers;
  }

  async req<T>({
    url,
    method = "GET",
    body,
    data,
    headers = {},
  }: RequestConfig): Promise<ApiResponse<T>> {
    try {
      const allHeaders = await this.getHeaders();

      // Support both 'body' and 'data' parameters (data is an alias for body)
      const requestBody = body || data;

      // Check if body is FormData - if so, don't JSON.stringify and remove Content-Type header
      const isFormData = requestBody instanceof FormData;

      const requestConfig: RequestInit = {
        method,
        headers: isFormData
          ? {
              // For FormData, only include Authorization header, let browser set Content-Type with boundary
              ...Object.fromEntries(
                Object.entries({ ...allHeaders, ...headers }).filter(
                  ([key]) => key.toLowerCase() !== 'content-type'
                )
              )
            }
          : {
              ...allHeaders,
              ...headers,
            },
        credentials: 'include', // CRITICAL: This fixes CORS issues with authentication
        cache: "no-store",
      };

      if (requestBody) {
        // Don't JSON.stringify FormData - send it as-is
        requestConfig.body = isFormData ? requestBody : JSON.stringify(requestBody);
      }

      const fullUrl = `${this.baseUrl}${url}`;
      const response = await fetch(fullUrl, requestConfig);

      if (!response.ok) {
        // Handle 401 unauthorized - redirect to login
        if (response.status === 401) {
          // Clear auth data
          document.cookie = 'crm_auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
          if (typeof window !== 'undefined') {
            localStorage.removeItem('crm_user');
            sessionStorage.clear();
            // Redirect to login using environment variable
            const logoutRedirectUrl = import.meta.env.VITE_LOGOUT_REDIRECT_URL || '/login';
            window.location.href = logoutRedirectUrl;
          }
          throw new Error('Unauthorized - redirected to login');
        }

        let errorData;
        try {
          errorData = await response.json();
        } catch {
          // If JSON parsing fails, use status text
          throw new Error(
            `HTTP error! status: ${response.status}, ${response.statusText}`
          );
        }

        // Throw with more detailed error information
        throw new Error(
          errorData.error ||
          errorData.message ||
          `HTTP error! status: ${response.status}, ${response.statusText}`
        );
      }

      // Parse and return JSON data
      const responseData = await response.json();
      return responseData as ApiResponse<T>;

    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message);
      }
      throw new Error("An unexpected error occurred during the API request");
    }
  }

  // Helper methods with improved typing
  async get<T>(
    url: string,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.req<T>({ url, method: this.Method.GET, headers });
  }

  async post<T>(
    url: string,
    body: unknown,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.req<T>({ url, method: this.Method.POST, body, headers });
  }

  async put<T>(
    url: string,
    body: unknown,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.req<T>({ url, method: this.Method.PUT, body, headers });
  }

  async delete<T>(
    url: string,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.req<T>({ url, method: this.Method.DELETE, headers });
  }

  async patch<T>(
    url: string,
    body: unknown,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.req<T>({ url, method: this.Method.PATCH, body, headers });
  }
}

const api = new Api();
export default api;